/**
 * WordPress dependencies
 */
import { store } from '@wordpress/interactivity';

/**
 * Convert UTC datetime to user's local time
 *
 * @param {string} utcDatetime - UTC datetime string (e.g., "2025-07-30T16:00:00Z")
 * @return {string|null} Formatted local time or null if invalid
 */
function convertUtcToLocalTime(utcDatetime) {
  if (!utcDatetime) {
    return null;
  }

  try {
    const utcDate = new Date(utcDatetime);

    // Check if the date is valid
    if (isNaN(utcDate.getTime())) {
      return null;
    }

    // Use Intl.DateTimeFormat to get user's local time
    return utcDate.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  } catch (error) {
    return null;
  }
}

/**
 * Convert UTC datetime to user's local date
 *
 * @param {string} utcDatetime - UTC datetime string (e.g., "2025-07-30T16:00:00Z")
 * @return {string|null} Formatted local date or null if invalid
 */
function convertUtcToLocalDate(utcDatetime) {
  if (!utcDatetime) {
    return null;
  }

  try {
    const utcDate = new Date(utcDatetime);

    // Check if the date is valid
    if (isNaN(utcDate.getTime())) {
      return null;
    }

    // Use Intl.DateTimeFormat to get user's local date in "Friday 15 August 2025" format
    return utcDate.toLocaleDateString(undefined, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  } catch (error) {
    return null;
  }
}

/**
 * Format match time matching PHP Match_Data_Utils::format_match_time
 *
 * @param {number} timeMinutes - Time in minutes
 * @param {number} periodId    - Period ID (1 or 2)
 * @return {string} Formatted time
 */
function formatMatchTime(timeMinutes, periodId) {
  const minutes = parseInt(timeMinutes, 10);
  const period = parseInt(periodId, 10);

  // First half injury time (period 1, more than 45 minutes)
  if (period === 1 && minutes > 45) {
    const addedTime = minutes - 45;
    return `45+${addedTime}`;
  }

  // Second half injury time (period 2, more than 90 minutes)
  if (period === 2 && minutes > 90) {
    const addedTime = minutes - 90;
    return `90+${addedTime}`;
  }

  // Regular time
  return String(minutes);
}

/**
 * Group goalscorers by player name, matching PHP logic
 *
 * @param {Array} goalscorers - Array of goal objects
 * @return {Array} Grouped goalscorers with player names and times
 */
function groupGoalscorers(goalscorers) {
  if (!Array.isArray(goalscorers) || goalscorers.length === 0) {
    return [];
  }

  const goalsByPlayer = {};

  goalscorers.forEach((goal) => {
    const scorer = goal.scorer;
    if (!goalsByPlayer[scorer]) {
      goalsByPlayer[scorer] = [];
    }
    goalsByPlayer[scorer].push(goal);
  });

  return Object.entries(goalsByPlayer).map(([scorer, goals]) => {
    const times = goals
      .map((goal) => {
        if (goal.time !== undefined && goal.time !== null && goal.time !== '') {
          const formattedTime = formatMatchTime(goal.time, goal.period || 1);
          return formattedTime + "'";
        }
        return null;
      })
      .filter(Boolean);

    return {
      scorer,
      times,
      timesFormatted: times.join(', '),
    };
  });
}

/**
 * Fetch match data from the REST API
 *
 * @param {string}  matchId   - The match ID to fetch
 * @param {boolean} cacheBust - Whether to add cache-busting parameter (for polling)
 * @return {Promise<Object>} Match data and hasLiveData flag
 */
async function fetchMatchData(matchId, cacheBust = false) {
  const url = new URL(
    `/wp-json/big-bite-stats-perform/v1/match/${matchId}`,
    window.location.origin,
  );

  // Add cache-busting timestamp for polling requests to bypass WPE object cache
  if (cacheBust) {
    url.searchParams.append('_', Math.floor(Date.now() / 1000));
  }

  const nonce = window.wpApiSettings?.nonce;

  const response = await fetch(url.toString(), {
    headers: {
      'Content-Type': 'application/json',
      'X-WP-Nonce': nonce,
    },
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();
  const hasLiveData = response.headers.get('X-Live-Data') === 'true';

  return { data, hasLiveData };
}

/**
 * Format timestamp as localized string
 *
 * @param {number} timestamp - Unix timestamp in seconds
 * @param {string} locale    - Locale string
 * @return {string} Formatted date string
 */
function formatTimestamp(timestamp, locale) {
  const date = new Date(timestamp * 1000);
  const options = {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  };
  return date.toLocaleString(locale || 'en-US', options);
}

/**
 * Update state with match data
 *
 * @param {Object} state     - Interactivity API state
 * @param {Object} matchData - Match data from API
 */
function updateStateWithMatchData(state, matchData) {
  // Update last updated timestamp
  const timestamp = matchData.timestamp || Math.floor(Date.now() / 1000);
  if (timestamp) {
    state.lastUpdated = formatTimestamp(timestamp, state.locale);
  }

  state.matchData = matchData;

  // Update live status
  state.isLive = matchData.status === 'live';

  // Update scores
  if (matchData.home_score !== undefined && matchData.home_score !== null) {
    state.homeScore = matchData.home_score;
  }
  if (matchData.away_score !== undefined && matchData.away_score !== null) {
    state.awayScore = matchData.away_score;
  }

  // Update goalscorers
  if (matchData.goalscorers) {
    state.groupedGoalscorersHome = groupGoalscorers(matchData.goalscorers.home || []);
    state.groupedGoalscorersAway = groupGoalscorers(matchData.goalscorers.away || []);
  }
}

store('big-bite-stats-perform/match-header', {
  state: {
    // Client-side only state (not initialized from PHP)
    loading: false,
    error: null,
    pollingIntervalId: null,
    isLivePollingActive: false,
    lastUpdated: '',
    matchData: null,
    // All other state (matchId, isLive, locale, homeScore, awayScore)
    // is initialized from PHP via wp_interactivity_state()
  },
  actions: {
    /**
     * Start synchronized polling on 60-second boundaries
     */
    async startLivePolling() {
      const { state, actions } = store('big-bite-stats-perform/match-header');

      // Don't start if already polling
      if (state.isLivePollingActive) {
        return;
      }

      state.isLivePollingActive = true;

      // Calculate next 60-second boundary for synchronized polling
      const now = Date.now();
      const pollingInterval = 60000; // 60 seconds
      const nextBoundary = Math.ceil(now / pollingInterval) * pollingInterval;
      const delayToNextBoundary = nextBoundary - now;

      // Fire immediate poll to catch any updates since page load
      await actions.pollForLiveUpdates();

      // Schedule synchronized polling at 60-second boundaries
      setTimeout(() => {
        state.pollingIntervalId = setInterval(async () => {
          await actions.pollForLiveUpdates();
        }, pollingInterval);
      }, delayToNextBoundary);
    },

    /**
     * Stop polling for live match updates
     */
    stopLivePolling() {
      const { state } = store('big-bite-stats-perform/match-header');

      if (state.pollingIntervalId) {
        clearInterval(state.pollingIntervalId);
        state.pollingIntervalId = null;
        state.isLivePollingActive = false;
      }
    },

    /**
     * Poll for live match updates
     */
    async pollForLiveUpdates() {
      const { state, actions } = store('big-bite-stats-perform/match-header');

      if (!state.matchId || state.loading) {
        return;
      }

      state.loading = true;
      state.error = null;

      try {
        // Fetch with cache busting to bypass WPE object cache
        const { data, hasLiveData } = await fetchMatchData(state.matchId, true);

        if (data && data.success && data.data) {
          const matchData = data.data;

          // Update all state with new match data
          updateStateWithMatchData(state, matchData);

          // Stop polling if match is no longer live
          if (!hasLiveData || !state.isLive) {
            actions.stopLivePolling();
          }
        }

        state.loading = false;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching match data:', error);
        state.error = error.message;
        state.loading = false;
      }
    },
  },
  callbacks: {
    init() {
      const { state, actions } = store('big-bite-stats-perform/match-header');

      // Store user's timezone in cookie for server-side rendering on future visits
      try {
        const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (userTimezone) {
          const maxAge = 60 * 60 * 24 * 30; // 30 days
          document.cookie = `user_timezone=${encodeURIComponent(userTimezone)}; path=/; max-age=${maxAge}; SameSite=Lax`;
        }
      } catch (error) {
        // Ignore timezone detection errors
      }

      // Format initial "Last updated" timestamp if not already formatted server-side
      if (state.lastUpdatedTimestamp && !state.lastUpdated) {
        state.lastUpdated = formatTimestamp(state.lastUpdatedTimestamp, state.locale);
      }

      // Find all time elements with UTC datetime and convert them
      const timeElements = document.querySelectorAll(
        '.wp-block-big-bite-stats-perform-match-header__time[data-utc-datetime]',
      );

      timeElements.forEach((element) => {
        const utcDatetime = element.getAttribute('data-utc-datetime');
        const fallbackTime = element.getAttribute('data-fallback-time');
        const currentText = element.textContent.trim();

        if (utcDatetime) {
          const localTime = convertUtcToLocalTime(utcDatetime);

          // Only update if different (avoids flash when server already converted)
          if (localTime && localTime !== currentText) {
            element.textContent = localTime;
          } else if (!localTime && fallbackTime && fallbackTime !== currentText) {
            element.textContent = fallbackTime;
          }
        }
      });

      // Find all date elements with UTC datetime and convert them
      const dateElements = document.querySelectorAll(
        '.wp-block-big-bite-stats-perform-match-header__date[data-utc-datetime]',
      );

      dateElements.forEach((element) => {
        const utcDatetime = element.getAttribute('data-utc-datetime');
        const fallbackDate = element.getAttribute('data-fallback-date');
        const currentText = element.textContent.trim();

        if (utcDatetime) {
          const localDate = convertUtcToLocalDate(utcDatetime);

          // Only update if different (avoids flash when server already converted)
          if (localDate && localDate !== currentText) {
            element.textContent = localDate;
          } else if (!localDate && fallbackDate && fallbackDate !== currentText) {
            element.textContent = fallbackDate;
          }
        }
      });

      // Start polling if match is live (based on server-rendered state)
      if (state.isLive) {
        actions.startLivePolling();
      }

      // Clean up polling interval when page unloads
      window.addEventListener('beforeunload', () => {
        actions.stopLivePolling();
      });

      // Handle page restoration from bfcache (browser back/forward)
      window.addEventListener('pageshow', (event) => {
        // event.persisted is true when page is restored from bfcache
        if (event.persisted && state.isLive) {
          // Restart polling if match was live
          actions.startLivePolling();
        }
      });
    },
  },
});
